import { Lock01 } from '@untitled-ui/icons-react/build/cjs';
import { orderBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import TableCmp from 'src/components/TableCmp';
import { ROUTES } from 'src/constants/routes';
import { activeQuoteColumnsData } from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';
import WalToast from 'src/utils/WalToast';

import { addActiveQuoteTag, isExpiredQuoteRate } from '../QuoteDetail/quoteDetails.helper';
import SelectQuoteTableRow from '../QuoteDetail/SelectQuoteTableRow';

import QuotePreparingLoader from './QuotePreparingLoader';
import { IQuotePreparingLaneContext } from './QuotePreparingLoader/laneInsights';
import SelectQuoteLoaderRow from './SelectQuoteLoaderRow';

export interface QuoteData {
    id?: string | number;
    carrierName?: string;
    carrierLogo?: string;
    deliveryType?: 'fastest' | 'standard' | 'economy' | string;
    transitTime?: string;
    estimatedDelivery?: string;
    totalCost?: string;
    [key: string]: any;
}

export type QuoteColumns = Record<string, boolean>;

interface SelectQuoteBlockProps {
    tableDataArr?: QuoteData[];
    onBookOrder?: (data?: QuoteData) => void;
    numberOfSkeletonRows?: number;
    isLoading?: boolean;
    isToBookStatus?: boolean;
    pickupDate?: string;
    onDuplicate?: (data: QuoteData) => void;
    onRequestRate?: (data: QuoteData) => void;
    /**
     * Public share-link viewers can't act on quotes — hides the Book Order
     * button (top-right) and the per-row selection radio. Row click is also
     * disabled so there's no implicit selection state.
     */
    isReadOnly?: boolean;
    /**
     * Shipment/lane context for the "Preparing your quote" loader — see
     * QuotePreparingLoader. May arrive empty/partial on first render (e.g. a
     * hard refresh has no router nav state yet); the loader renders
     * progressively as fields fill in, rather than swapping components.
     */
    laneContext?: IQuotePreparingLaneContext | null;
    /**
     * True only when the customer was just redirected here from creating a
     * quote. Shows "Preparing your quote" immediately for the whole wait
     * instead of the generic table skeleton — for that entry path only, never
     * on a plain list-page navigation, hard refresh, or other re-fetch.
     */
    isNewQuote?: boolean;
}

// Row count for the API-call-in-flight skeleton — matches the typical number
// of rate options a quote comes back with, so the table doesn't visibly
// shrink/grow when real data lands.
const LOADING_SKELETON_ROW_COUNT = 5;

const DUMMY_QUOTES: QuoteData[] = [
    { id: 1, carrierName: 'XPO Logistics', deliveryType: 'fastest', shippingMode: 'LTL', shippingServiceLevel: 'Standard', projectedDeliveryDate: '2026-06-04T00:00:00Z', finalCharge: 1284.5 },
    { id: 2, carrierName: 'FedEx Freight', deliveryType: 'standard', shippingMode: 'LTL', shippingServiceLevel: 'Economy', projectedDeliveryDate: '2026-06-06T00:00:00Z', finalCharge: 987.0 },
    { id: 3, carrierName: 'Estes Express', deliveryType: 'economy', shippingMode: 'LTL', shippingServiceLevel: 'Standard', projectedDeliveryDate: '2026-06-08T00:00:00Z', finalCharge: 762.25 },
    { id: 4, carrierName: 'Old Dominion', deliveryType: 'standard', shippingMode: 'LTL', shippingServiceLevel: 'Premium', projectedDeliveryDate: '2026-06-05T00:00:00Z', finalCharge: 1105.75 },
];

// Full set of all possible columns (including controllable ones).
// To add a new controllable column: add it here and update QUOTE_COLUMN_TO_CELL_ID.
const ALL_HEAD_CELLS = [
    {
        id: 'no',
        name: 'No.',
        rowClassName: 'min-w-[82px] max-w-[82px]',
        sortable: false,
    },
    {
        id: 'carrierName',
        name: 'Carrier',
        rowClassName: 'min-w-[170px] max-w-[170px]',
        sortable: true,
    },
    {
        id: 'deliveryType',
        name: 'Tags',
        rowClassName: 'min-w-[100px] max-w-[100px]',
        sortable: false,
    },
    {
        id: 'shippingMode',
        name: 'Mode',
        rowClassName: 'min-w-[82px] max-w-[82px]',
        sortable: false,
    },
    {
        id: 'shippingServiceLevel',
        name: 'Service Level',
        rowClassName: 'min-w-[108px] max-w-[108px]',
        sortable: false,
    },
    {
        // Sort by projectedDeliveryDate — earlier date = fewer transit days
        id: 'projectedDeliveryDate',
        name: 'Transit Time',
        rowClassName: 'min-w-[108px] max-w-[108px]',
        sortable: true,
    },
    {
        id: 'finalCharge',
        name: 'Total Cost',
        rowClassName: 'min-w-[108px] max-w-[108px]',
        sortable: true,
    },
    // Client decision — green checkmark shown only once a quote is actually booked/to-book (NOT
    // for the live radio selection while still choosing — showing it earlier was reported as a
    // bug). Always present (not a controllable column — never listed in QUOTE_COLUMN_TO_CELL_ID),
    // right after Total Cost, ahead of the existing trailing radio/order-id columns (unchanged).
    {
        id: 'selectedIndicator',
        name: '',
        rowClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
        sortable: false,
    },
];

// Maps quoteColumns API key → head cell id.
// Add a new entry here when a new controllable column is introduced.
const QUOTE_COLUMN_TO_CELL_ID: Record<string, string> = {
    carrierName: 'carrierName',
    transitTime: 'projectedDeliveryDate',
    shippingMode: 'shippingMode',
    shippingServiceLevel: 'shippingServiceLevel',
};

const SelectQuoteBlock: React.FC<SelectQuoteBlockProps> = ({
    tableDataArr,
    onBookOrder,
    numberOfSkeletonRows = 5,
    isLoading: externalIsLoading = true,
    isToBookStatus = false,
    pickupDate,
    onDuplicate,
    onRequestRate,
    isReadOnly = false,
    laneContext,
    isNewQuote = false,
}) => {
    const { t } = useTranslation();
    const reduxActiveQuotes = useSelector((state: any) => state.quoting.activeQuotes ?? []);
    const reduxActiveQuoteColumns = useSelector(activeQuoteColumnsData);
    const { appearance } = useSelector((state: RootState) => state.basic);
    const userTimezone = appearance?.timezone || 'UTC';

    const [activeQuoteParams, setActiveQuoteParams] = useState<{
        sortField: string;
        sortType: 'asc' | 'desc';
    }>({ sortField: 'finalCharge', sortType: 'asc' });

    // User decision — customer can pick only ONE quote (radio, not checkbox), so selection is a
    // single id, not an array. Book Order stays disabled until exactly one is chosen.
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const handleSelectRow = useCallback((id: number) => {
        setSelectedId(id);
    }, []);

    // Use tableDataArr if provided (for backward compatibility), otherwise use Redux-cached quotes
    const rawDisplayData = tableDataArr || reduxActiveQuotes;
    const displayData = useMemo(() => {
        const dataWithTags = addActiveQuoteTag(rawDisplayData);

        return orderBy(dataWithTags, activeQuoteParams.sortField, activeQuoteParams.sortType);
    }, [rawDisplayData, activeQuoteParams]);
    const hasData = displayData && displayData.length > 0;

    // Same "only show the column when at least one row actually needs it" pattern as Orders'
    // hasPriorityOrder (src/app/Orders/index.tsx) — the rateAction cell (3-dot menu / info icon)
    // only ever renders content for expired rates, so reserving its 48px on every row left a dead
    // gap ahead of the trailing radio column whenever nothing in the current batch was expired.
    const hasExpiredRate = useMemo(() => displayData.some((quote) => isExpiredQuoteRate(quote, userTimezone)), [displayData, userTimezone]);

    // Loading has two distinct states:
    // - externalIsLoading (the getActiveQuotes call is in flight, including a
    //   later socket-triggered re-fetch): generic table skeleton, same pattern
    //   used across other tables in the app. EXCEPT isNewQuote — freshly
    //   created quotes show "Preparing your quote" for the whole wait,
    //   skipping the generic skeleton entirely (that entry path only).
    // - !externalIsLoading && !hasData (a fetch resolved with zero results,
    //   e.g. rates haven't landed yet): "Preparing your quote" shown
    //   indefinitely — never a bare "no quotes" dead-end. If a later re-fetch
    //   lands real data, hasData flips true and plays a completion beat
    //   (isPreparingSettling) before the real table crossfades in.
    const [showPreparingLoader, setShowPreparingLoader] = useState(isNewQuote || (!externalIsLoading && !hasData));
    const [isPreparingSettling, setIsPreparingSettling] = useState(false);

    useEffect(() => {
        if (isNewQuote) {
            if (hasData) {
                if (showPreparingLoader && !isPreparingSettling) setIsPreparingSettling(true);
            } else {
                setShowPreparingLoader(true);
            }

            return;
        }

        if (externalIsLoading) {
            setShowPreparingLoader(false);
            setIsPreparingSettling(false);
        } else if (!hasData) {
            setShowPreparingLoader(true);
            setIsPreparingSettling(false);
        } else if (showPreparingLoader && !isPreparingSettling) {
            setIsPreparingSettling(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalIsLoading, hasData, isNewQuote]);

    // Book Order is only enabled once a quote is selected
    const quoteToBook = useMemo(() => (selectedId !== null ? displayData.find((q) => Number(q.id) === selectedId) ?? null : null), [selectedId, displayData]);
    const isQuoteToBookExpired = useMemo(() => (quoteToBook ? isExpiredQuoteRate(quoteToBook, userTimezone) : false), [quoteToBook, userTimezone]);

    const handleBookOrderClick = useCallback(() => {
        if (isQuoteToBookExpired) {
            WalToast.error(t('MyQuote.Carrier_Quote_Expired_Book_Blocked'));

            return;
        }

        onBookOrder?.(quoteToBook ?? undefined);
    }, [isQuoteToBookExpired, onBookOrder, quoteToBook, t]);

    // Compute which head cells to show based on quoteColumns visibility config.
    // Columns not listed in QUOTE_COLUMN_TO_CELL_ID are always visible.
    const visibleHeadCells = useMemo(() => {
        const cells = [
            // 'selectedIndicator' (booked checkmark) only ever renders content once a quote is
            // actually booked/to-book — on the still-choosing "Select a Quote" screen every row's
            // cell is empty, leaving a dead ~50px gap ahead of the trailing radio column. Drop the
            // column entirely until isToBookStatus is true; SelectQuoteTableRow/SelectQuoteLoaderRow
            // gate the matching cell on their hideBookOrderButton prop, which is this same value.
            ...ALL_HEAD_CELLS.filter((cell) => cell.id !== 'selectedIndicator' || isToBookStatus),
            // Client decision — the Order ID column moved to the page header (a linked "#<order
            // code>" button next to Quote Actions — see QuoteDetail/index.tsx) instead of living in
            // this table. The trailing column is now ONLY the expired-rate action menu, so it's
            // dropped entirely once booked (isToBookStatus) — that menu never applied there anyway,
            // since Duplicate/Re-quote only make sense while still choosing a rate.
            ...(isReadOnly || isToBookStatus || !(hasExpiredRate || externalIsLoading)
                ? []
                : [
                      {
                          // Client decision — column used to reserve 90-100px for the old "Order ID
                          // + copy button" display; now it only ever holds a single small icon
                          // (3-dot menu / info icon, expired rates only), so the reserved width was
                          // leaving a large blank gap on every non-expired row. Shrunk to match, and
                          // dropped entirely (see hasExpiredRate above) when nothing in the current
                          // batch is expired — kept during externalIsLoading only to avoid the
                          // header flickering narrower once real data lands.
                          id: 'rateAction',
                          name: '',
                          sortable: false,
                          rowClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
                      },
                  ]),
            // User decision — single-select radio, moved to the right end (was a leading
            // multi-select checkbox column) — only in interactive (non-read-only, non-booked) mode.
            ...(!isReadOnly && !isToBookStatus
                ? [
                      {
                          id: 'selectQuote',
                          name: '',
                          sortable: false,
                          visible: true,
                          rowClassName: '!pl-2 w-[40px] min-w-[40px] max-w-[40px]',
                      },
                  ]
                : []),
        ];

        if (!reduxActiveQuoteColumns) return cells;

        const hiddenCellIds = new Set(
            Object.entries(reduxActiveQuoteColumns)
                .filter(([, visible]) => visible === false)
                .map(([key]) => QUOTE_COLUMN_TO_CELL_ID[key])
                .filter(Boolean)
        );

        return cells.filter((cell) => !hiddenCellIds.has(cell.id));
    }, [isToBookStatus, isReadOnly, reduxActiveQuoteColumns, hasExpiredRate, externalIsLoading]);

    // Mirrors the render branches below so the header hides only while the
    // "Preparing your quote" branch is the one actually on screen.
    const isReadOnlyGateVisible = isReadOnly && !externalIsLoading && hasData;
    const isTableSkeletonVisible = !isReadOnlyGateVisible && externalIsLoading && !isNewQuote;
    const isPreparingLoaderVisible = !isReadOnlyGateVisible && !isTableSkeletonVisible && showPreparingLoader;

    return (
        <div className="w-full table-box-wrap">
            {!isPreparingLoaderVisible && (
                <div className="table-top-header">
                    <div className="table-left-wrap ">
                        <div className={`table-title-wrap`}>
                            <h5 className="text-textSecondary text-sm font-semibold table-title">{t('QuotingHub.SelectQuote.Title')}</h5>
                        </div>
                    </div>
                    {!isToBookStatus && !isReadOnly && (
                        <div className="table-right-wrap">
                            <ButtonCmp className="btn_secondary_black" disabled={selectedId === null} onClick={handleBookOrderClick}>
                                {t('QuotingHub.SelectQuote.Book_Order')}
                            </ButtonCmp>
                        </div>
                    )}
                </div>
            )}
            <div className="">
                {isReadOnlyGateVisible ? (
                    /* ── Read-only login gate: blurred skeleton + lock overlay ── */
                    <div className="relative overflow-hidden">
                        <div className="blur-sm pointer-events-none max-h-[192px] overflow-hidden select-none" aria-hidden="true">
                            <div className="overflow-x-auto">
                                <TableCmp
                                    headCells={visibleHeadCells}
                                    tableDataArr={DUMMY_QUOTES}
                                    TableRowCmp={SelectQuoteTableRow}
                                    TableLoaderRowCmp={SelectQuoteLoaderRow}
                                    params={activeQuoteParams}
                                    setParams={setActiveQuoteParams}
                                    tableBodyRowClass="last:border-b-0"
                                    tableRowCmpProps={{ isReadOnly: true, hasExpiredRate }}
                                    isTableDataLoading={false}
                                    numberOfSkeletonRows={DUMMY_QUOTES.length}
                                    tableRowHeight="xs"
                                />
                            </div>
                        </div>
                        <div className="absolute inset-0 flex flex-col items-center h-[192px] bg-white/60 justify-center backdrop-blur-[2px] z-10 rounded-b-lg">
                            <NotFoundUI
                                icon={<Lock01 className="w-5 h-5 text-primary" />}
                                iconClassName="!bg-primary/10 !border-primary/20 !rounded-full !shadow-none !hidden"
                                title="Login to view quotes"
                                desc="Log in to access available carrier options and pricing details"
                                descClass="!whitespace-nowrap"
                                containerClassName="!my-0 !mx-0"
                                buttonText="Login to access"
                                buttonClassName="btn-primary"
                                buttonType="btn-outline-primary"
                                handleActionType={() => (window.location.href = ROUTES.LOGIN)}
                                isEmptyBackground={true}
                            />
                        </div>
                    </div>
                ) : isTableSkeletonVisible ? (
                    /* ── API call in flight: generic table skeleton, same pattern as other tables ── */
                    <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                        <TableCmp
                            headCells={visibleHeadCells}
                            tableDataArr={[]}
                            TableRowCmp={SelectQuoteTableRow}
                            TableLoaderRowCmp={SelectQuoteLoaderRow}
                            params={activeQuoteParams}
                            setParams={setActiveQuoteParams}
                            tableBodyRowClass="last:border-b-0"
                            tableRowCmpProps={{
                                quoteColumns: reduxActiveQuoteColumns,
                                showSelectColumn: !isReadOnly && !isToBookStatus,
                                hideBookOrderButton: isToBookStatus,
                                isReadOnly,
                            }}
                            numberOfSkeletonRows={LOADING_SKELETON_ROW_COUNT}
                            isTableDataLoading
                            tableRowHeight="xs"
                        />
                    </div>
                ) : isPreparingLoaderVisible ? (
                    <QuotePreparingLoader laneContext={laneContext} isSettling={isPreparingSettling} onSettled={() => setShowPreparingLoader(false)} />
                ) : (
                    <div className="relative qpl-fade-in">
                        <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                            <TableCmp
                                headCells={visibleHeadCells}
                                tableDataArr={displayData}
                                TableRowCmp={SelectQuoteTableRow}
                                TableLoaderRowCmp={SelectQuoteLoaderRow}
                                params={activeQuoteParams}
                                setParams={setActiveQuoteParams}
                                tableBodyRowClass="last:border-b-0 [&>td:first-child]:!rounded-bl-lg [&>td:last-child]:!rounded-br-lg"
                                tableRowCmpProps={{
                                    hideBookOrderButton: isToBookStatus,
                                    isHighlighted: isToBookStatus,
                                    quotePickupDate: pickupDate,
                                    quoteColumns: reduxActiveQuoteColumns,
                                    onDuplicate,
                                    onRequestRate,
                                    isReadOnly,
                                    selectedId,
                                    onSelect: handleSelectRow,
                                    hasExpiredRate,
                                }}
                                isTableRowClickable={!isToBookStatus && !isReadOnly}
                                onRowClick={!isToBookStatus && !isReadOnly ? (row) => handleSelectRow(Number(row.id)) : undefined}
                                numberOfSkeletonRows={numberOfSkeletonRows}
                                isTableDataLoading={externalIsLoading}
                                tableRowHeight="xs"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectQuoteBlock;
